import React from "react";

// Mui Styles
import { StyledPaper } from "@/styles/mui/globalStyles";
import { itItemListTheme } from "@/styles/mui/tableListTheme";
import { ThemeProvider } from "@mui/material/styles";

//Components
import Layout from "@/components/layout";
import Title from "@/components/title";
import LoadingButton from "@mui/lab/LoadingButton";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid2";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

//mui Icon
import SaveIcon from "@mui/icons-material/Save";

// CSS
import "@/styles/page/systemSettingProductPage.css";

// Navigate
import { useNavigate, useSearchParams } from "react-router-dom";

//hooks
import { useCurrentAction } from "@/hooks/user";

// Fetch
import {
  getDepartmemtMember,
  getDepartmemts,
  getItItemAccess,
  getItItems,
  updateItItemAccess,
} from "@/fetch/fetchItItem";

import _ from "lodash";

const ItItemListPage = (props) => {
  const { open, setOpen } = props;

  // Navigate
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const department_id = searchParams.get("department_id") ?? null;
  // States
  const [members, setMembers] = React.useState({ name: "", member: [] });
  const [itItems, setItItems] = React.useState([]);
  const [itItemAccess, setItItemAccess] = React.useState([]);
  const [filterCategory, setFilterCategory] = React.useState([]);
  const [edit, setEdit] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [depart, setDepart] = React.useState({ options: [], value: "" });
  const [snackSeverity, setSnackSeverity] = React.useState("success");
  const [snackMessage, setSnackMessage] = React.useState("");
  const [snackOpen, setSnackOpen] = React.useState(false);

  const actions = useCurrentAction();

  const handleChange = async (event) => {
    let answer = true;
    if (edit > 0) {
      answer = window.confirm(`你有未儲存的設定，要繼續並放棄已更改的設定嗎`);
    }
    if (answer) {
      setEdit(0);
      searchParams.set("department_id", event.target.value);
      setSearchParams(searchParams);
      setDepart((prev) => ({ ...prev, value: event.target.value }));
      try {
        const [{ data: memberData }, { data: itItem }, { data: itItemAccess }] =
          await Promise.all([
            getDepartmemtMember(event.target.value),
            getItItems(),
            getItItemAccess(),
          ]);

        setMembers(memberData);
        setItItems(itItem);
        setItItemAccess(itItemAccess);
      } catch (err) {
        console.log(err);
      }
    }
  };

  const fetchAll = async () => {
    try {
      const { data: departData } = await getDepartmemts(actions.viewAll);
      const [{ data: memberData }, { data: itItem }, { data: itItemAccess }] =
        await Promise.all([
          getDepartmemtMember(department_id ?? departData[0].id),
          getItItems(),
          getItItemAccess(),
        ]);
      setDepart((prev) => ({
        ...prev,
        value: department_id ?? departData[0].id,
        options: departData,
      }));
      setMembers(memberData);
      setItItems(itItem);
      setItItemAccess(itItemAccess);
      setFilterCategory(itItem.map((item) => item.category));
    } catch (err) {
      console.log(err);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const { data: result } = await updateItItemAccess(
        itItemAccess,
        depart.value
      );
      if (result) {
        setSnackMessage("更新成功");
        setSnackSeverity("success");
        setSnackOpen(true);
        setEdit((prev) => (prev = 0));
        fetchAll();
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // Browser Back
  const handleBeforeUnload = (e) => {
    e.preventDefault();
    const message =
      "Are you sure you want to leave? All provided data will be lost.";
    e.returnValue = message;
    return message;
  };

  React.useEffect(() => {
    fetchAll();
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterItItems = itItems.filter((itItem) =>
    filterCategory.includes(itItem.category)
  );

  return (
    <Layout
      header="StaffManagePage"
      snackOpen={snackOpen}
      snackClose={() => setSnackOpen(false)}
      snackMessage={snackMessage}
      snackSeverity={snackSeverity}
      open={open}
      setOpen={setOpen}
    >
      <Title
        title={`IT使用權限`}
        history={() => navigate(-1)}
        children={
          <div>
            {/* Save Record */}
            <LoadingButton
              onClick={handleSubmit}
              disabled={edit < 1 ? true : false}
              loading={loading}
              variant="outlined"
              loadingPosition="start"
              startIcon={<SaveIcon color={edit > 0 ? "info" : ""} />}
            >
              儲存
            </LoadingButton>
          </div>
        }
      />
      <StyledPaper page={"yes"} alignItems={"left"}>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 6,
            }}
          >
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">部門</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={depart.value}
                label="部門"
                onChange={handleChange}
              >
                {depart.options.map(({ id, name }, index) => (
                  <MenuItem key={index} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 6,
            }}
          >
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">filter</InputLabel>
              <Select
                multiple
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={filterCategory}
                label="filter"
                onChange={(event) => setFilterCategory(event.target.value)}
                input={<OutlinedInput label="Tag" />}
                renderValue={(selected) => selected.join(", ")}
              >
                {itItems.map((item, index) => (
                  <MenuItem key={index} value={item.category}>
                    <Checkbox
                      checked={filterCategory.includes(item.category)}
                    />
                    <ListItemText primary={item.category} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid container size={12}>
            <TableContainer component={Paper} sx={{ maxHeight: 550 }}>
              <Table
                stickyHeader
                sx={{
                  minWidth: 650,
                }}
                aria-label="simple table"
              >
                {filterItItems.length > 0 ? (
                  <React.Fragment>
                    <TableHead>
                      <TableRow>
                        <TableCell></TableCell>
                        {members.member.length > 0 &&
                          members.member.map((item, index) => (
                            <TableCell align="center" key={index}>
                              {item.alias}
                            </TableCell>
                          ))}
                      </TableRow>
                    </TableHead>
                    <ThemeProvider theme={{ itItemListTheme }}>
                      <TableBody>
                        {filterItItems.map((itItem, index) => {
                          return (
                            <React.Fragment>
                              <TableRow>
                                <TableCell key={index}>
                                  <b>{itItem.category}</b>
                                </TableCell>
                                {members.member.length > 0 &&
                                  members.member.map((member, index) => {
                                    // console.log("itItem", itItem);
                                    // console.log("member", member);

                                    const handleCategoryRecord = (
                                      __,
                                      value
                                    ) => {
                                      if (!actions.update) {
                                        window.confirm(
                                          "you don't have permission"
                                        );
                                        return false;
                                      }

                                      const copyItItemAccess = _.chain(
                                        itItemAccess
                                      )
                                        .groupBy(
                                          ({ users_id, it_item_id }) =>
                                            `${users_id}_${it_item_id}`
                                        )
                                        .mapValues((item) => item[0])
                                        .value();

                                      const addItem = _.chain(
                                        itItem.details.map((detail) => ({
                                          users_id: member.id,
                                          it_item_id: detail.id,
                                          item_permission: value,
                                        }))
                                      )
                                        .groupBy(
                                          ({ users_id, it_item_id }) =>
                                            `${users_id}_${it_item_id}`
                                        )
                                        .mapValues((item) => item[0])
                                        .value();

                                      const mergedItItemAccess = Object.values(
                                        _.merge({}, copyItItemAccess, addItem)
                                      );

                                      setItItemAccess(mergedItItemAccess);
                                      setEdit((prev) => prev + 1);
                                    };

                                    const isChecked = itItemAccess.filter(
                                      (itemAccess) => {
                                        let value = false;
                                        itItem.details.forEach((item) => {
                                          if (
                                            itemAccess.it_item_id === item.id &&
                                            itemAccess.users_id === member.id
                                          ) {
                                            value = true;
                                          }
                                        });
                                        return value;
                                      }
                                    );

                                    //This part is used to create the status of all child checkBox for defining the status of their parent checkBox
                                    const tempDetail = itItem.details.map(
                                      (item) => ({
                                        it_item_id: item.id,
                                        users_id: member.id,
                                        item_permission: false,
                                      })
                                    );

                                    const groupDetailsChecked = _.chain(
                                      tempDetail
                                    )
                                      .groupBy(
                                        (item) =>
                                          `${item.it_item_id}_${item.users_id}`
                                      )
                                      .mapValues((item) => item[0])
                                      .value();

                                    const groupIsChecked = _.chain(isChecked)
                                      .groupBy(
                                        (item) =>
                                          `${item.it_item_id}_${item.users_id}`
                                      )
                                      .mapValues((item) => item[0])
                                      .value();

                                    const mergedChecked = Object.values(
                                      _.merge(
                                        {},
                                        groupDetailsChecked,
                                        groupIsChecked
                                      )
                                    );

                                    return (
                                      <TableCell align="center" key={index}>
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              indeterminate={mergedChecked.reduce(
                                                (prev, _, index, source) => {
                                                  if (index > 0) {
                                                    prev = [
                                                      ...prev,
                                                      source[index]
                                                        .item_permission !==
                                                      source[index - 1]
                                                        .item_permission
                                                        ? true
                                                        : false,
                                                    ];
                                                  }
                                                  if (
                                                    index ===
                                                    source.length - 1
                                                  ) {
                                                    return prev.some(
                                                      (item) => item
                                                    );
                                                  }
                                                  return prev;
                                                },
                                                []
                                              )}
                                              checked={
                                                isChecked.length > 0 &&
                                                isChecked.every(
                                                  (item) => item.item_permission
                                                ) &&
                                                isChecked.length ===
                                                  itItem.details.length
                                              }
                                              onChange={handleCategoryRecord}
                                            />
                                          }
                                        ></FormControlLabel>
                                      </TableCell>
                                    );
                                  })}
                              </TableRow>
                              {itItem.details.map((itItemDetail, index) => {
                                return (
                                  <TableRow>
                                    <TableCell key={index}>
                                      &nbsp; - {itItemDetail.item_name}
                                    </TableCell>
                                    {members.member.length > 0 &&
                                      members.member.map((member, index) => {
                                        // console.log("detail", itItemDetail);
                                        // console.log("member", member);
                                        const targetItemIndex =
                                          itItemAccess.findIndex(
                                            (itemAccess) =>
                                              itemAccess.it_item_id ===
                                                itItemDetail.id &&
                                              itemAccess.users_id === member.id
                                          );

                                        const handleitItemRecord = () => {
                                          if (!actions.update) {
                                            window.confirm(
                                              "you don't have permission"
                                            );
                                            return false;
                                          }

                                          if (targetItemIndex > -1) {
                                            const copyItItemAccess = [
                                              ...itItemAccess,
                                            ];
                                            copyItItemAccess[
                                              targetItemIndex
                                            ].item_permission =
                                              !copyItItemAccess[targetItemIndex]
                                                .item_permission;
                                            setItItemAccess(copyItItemAccess);
                                          } else {
                                            const addItem = {
                                              id: genMaxId(itItemAccess) + 1,
                                              it_item_id: itItemDetail.id,
                                              users_id: member.id,
                                              item_permission: true,
                                            };
                                            setItItemAccess((prev) => [
                                              ...prev,
                                              addItem,
                                            ]);
                                          }
                                          setEdit((prev) => prev + 1);
                                        };

                                        return (
                                          <TableCell align="center" key={index}>
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  checked={
                                                    targetItemIndex !== -1
                                                      ? itItemAccess[
                                                          targetItemIndex
                                                        ].item_permission
                                                      : false
                                                  }
                                                  onChange={handleitItemRecord}
                                                />
                                              }
                                            ></FormControlLabel>
                                          </TableCell>
                                        );
                                      })}
                                  </TableRow>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </ThemeProvider>
                  </React.Fragment>
                ) : (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        沒有資料
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </StyledPaper>
    </Layout>
  );
};

export default ItItemListPage;

function genMaxId(arr) {
  const value = arr.reduce((prev, cur) => (cur.id > prev.id ? cur : prev), {
    id: 0,
  });
  const { id } = value;
  return id;
}
